import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield, TrendingUp, Building2 } from 'lucide-react';

export const HeroSection = () => {
    const { t } = useTranslation();

    const stats = [
        { value: '15+', label: t('hero.yearsExperience') },
        { value: '40+', label: t('hero.partnerBanks') },
        { value: t('hero.noLimit'), label: t('hero.financingAmount') },
    ];

    return (
        <section className="relative min-h-screen flex items-center overflow-hidden pt-20">
            {/* Background Elements */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
            <div className="absolute top-1/4 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
            
            {/* Decorative Pattern */}
            <div className="absolute inset-0 opacity-[0.02]" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23047857' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    {/* Left Content */}
                    <div className="text-left">
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
                            {t('hero.title1')}{' '}
                            <span className="text-gradient-emerald">{t('hero.title2')}</span>{' '}
                            {t('hero.title3')}
                        </h1>

                        <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-xl">
                            {t('hero.description')}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 mb-12">
                            <a href="#simulator">
                                <Button 
                                    size="lg" 
                                    className="btn-premium text-primary-foreground px-8 py-6 rounded-full text-base font-semibold group"
                                >
                                    <span className="relative z-10 flex items-center">
                                        {t('hero.startSimulator')}
                                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                                    </span>
                                </Button>
                            </a>
                            <a
                                href="https://www.casaconsulting.ch"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Button 
                                    variant="outline" 
                                    size="lg" 
                                    className="border-accent text-accent hover:bg-accent hover:text-accent-foreground px-8 py-6 rounded-full text-base font-semibold"
                                >
                                    <Building2 className="w-4 h-4 mr-2" />
                                    {t('nav.realEstate')}
                                </Button>
                            </a>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-6 pt-8 border-t border-border">
                            {stats.map((stat, index) => (
                                <div key={index} className="text-left">
                                    <div className="font-display text-2xl sm:text-3xl font-bold text-primary">
                                        {stat.value}
                                    </div>
                                    <div className="text-sm text-muted-foreground mt-1">
                                        {stat.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Content - Hero Image */}
                    <div className="relative hidden lg:block">
                        <div className="relative">
                            {/* Main Image */}
                            <div className="relative rounded-3xl overflow-hidden shadow-elegant">
                                <img
                                    src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=800&auto=format&fit=crop&q=80"
                                    alt="Professional Financial Consultant"
                                    className="w-full h-[500px] object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-foreground/30 via-transparent to-transparent" />
                            </div>

                            {/* Floating Card 1 */}
                            <div className="absolute -left-8 top-1/4 card-glass p-4 animate-float">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                                        <Shield className="w-5 h-5 text-primary-foreground" />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-foreground text-sm">{t('about.features.security')}</div>
                                        <div className="text-xs text-muted-foreground">Bank-level security</div>
                                    </div>
                                </div>
                            </div>

                            {/* Floating Card 2 */}
                            <div className="absolute -right-4 bottom-1/4 card-glass p-4 animate-float" style={{ animationDelay: '1s' }}>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
                                        <TrendingUp className="w-5 h-5 text-accent-foreground" />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-foreground text-sm">Best Rates</div>
                                        <div className="text-xs text-muted-foreground">{t('about.benefits.competitive')}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Decorative Elements */}
                            <div className="absolute -z-10 -bottom-6 -right-6 w-full h-full rounded-3xl border-2 border-primary/20" />
                            <div className="absolute -z-10 -bottom-12 -right-12 w-full h-full rounded-3xl bg-accent/10" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
